import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, X, ShoppingCart, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function Purchases() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/purchases").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="purchases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Inventory in</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Purchases</h1>
          <p className="text-sm text-muted-foreground mt-1">Receive stock from suppliers — each purchase auto-creates batches with cost prices.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-purchase-btn"><Plus className="w-4 h-4 mr-1" />New purchase</Button>
      </div>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Purchase #</TableHead><TableHead>Bill #</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(p => (
              <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                <TableCell className="font-mono font-semibold">{p.purchase_no}</TableCell>
                <TableCell className="text-sm">{p.bill_no || "—"}</TableCell>
                <TableCell className="text-sm">{p.bill_date}</TableCell>
                <TableCell className="text-sm">{p.supplier?.name || "—"}</TableCell>
                <TableCell className="text-sm">{p.items.length}</TableCell>
                <TableCell className="text-right font-mono">{inr(p.grand_total)}</TableCell>
                <TableCell><Badge variant={p.status === "paid" ? "secondary" : p.status === "partial" ? "outline" : "destructive"}>{p.status}</Badge></TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => setViewing(p)} data-testid={`view-purchase-${p.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No purchases yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <PurchaseDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <PurchaseDetail purchase={viewing} onClose={() => setViewing(null)} onDeleted={() => { setViewing(null); load(); }} />
    </div>
  );
}

function PurchaseDialog({ open, onOpenChange, onSaved }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({
    supplier_id: "", bill_no: "", bill_date: new Date().toISOString().slice(0, 10),
    items: [], other_charges: 0, discount: 0, paid_amount: 0, payment_method: "cash", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/suppliers").then(r => setSuppliers(r.data));
      api.get("/products").then(r => setProducts(r.data));
      setF({
        supplier_id: "", bill_no: "", bill_date: new Date().toISOString().slice(0, 10),
        items: [], other_charges: 0, discount: 0, paid_amount: 0, payment_method: "cash", notes: "",
      });
    }
  }, [open]);

  const addItem = () => setF(p => ({ ...p, items: [...p.items, { product_id: "", batch_no: "", expiry_date: "", qty: 1, cost_price: 0, mrp: "" }] }));
  const updateItem = (i, patch) => setF(p => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const removeItem = (i) => setF(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    const items_total = f.items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.cost_price) || 0), 0);
    const grand = items_total + (parseFloat(f.other_charges) || 0) - (parseFloat(f.discount) || 0);
    return { items_total, grand };
  }, [f.items, f.other_charges, f.discount]);

  const submit = async () => {
    if (f.items.length === 0) { toast.error("Add at least one item"); return; }
    if (f.items.some(it => !it.product_id || !it.qty || !it.cost_price)) { toast.error("Each item needs product, qty and cost price"); return; }
    setBusy(true);
    try {
      const payload = {
        supplier_id: f.supplier_id || null,
        bill_no: f.bill_no || null,
        bill_date: f.bill_date,
        items: f.items.map(it => ({
          product_id: it.product_id,
          batch_no: it.batch_no || null,
          expiry_date: it.expiry_date || null,
          qty: parseInt(it.qty),
          cost_price: parseFloat(it.cost_price),
          mrp: it.mrp ? parseFloat(it.mrp) : null,
        })),
        other_charges: parseFloat(f.other_charges) || 0,
        discount: parseFloat(f.discount) || 0,
        paid_amount: parseFloat(f.paid_amount) || 0,
        payment_method: f.payment_method,
        notes: f.notes,
      };
      const { data } = await api.post("/purchases", payload);
      toast.success(`Purchase ${data.purchase_no} recorded · ${data.items.length} batches created`);
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="purchase-dialog">
        <DialogHeader><DialogTitle>New purchase</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Supplier</Label>
            <Select value={f.supplier_id || "_none"} onValueChange={v => setF(p => ({ ...p, supplier_id: v === "_none" ? "" : v }))}>
              <SelectTrigger data-testid="purchase-supplier"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Walk-in supplier</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Bill #</Label><Input value={f.bill_no} onChange={e => setF(p => ({ ...p, bill_no: e.target.value }))} data-testid="purchase-billno" /></div>
          <div><Label>Bill date</Label><Input type="date" value={f.bill_date} onChange={e => setF(p => ({ ...p, bill_date: e.target.value }))} /></div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Items received</div>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="purchase-add-item"><Plus className="w-3 h-3 mr-1" />Add item</Button>
          </div>
          {f.items.length === 0 && <div className="text-sm text-muted-foreground p-3 text-center border border-dashed rounded">No items yet.</div>}
          <div className="space-y-2">
            {f.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`purchase-item-${i}`}>
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-xs">Product</Label>
                  <Select value={it.product_id} onValueChange={v => {
                    const p = products.find(p => p.id === v);
                    updateItem(i, { product_id: v, cost_price: it.cost_price || p?.cost_price || 0, mrp: it.mrp || p?.mrp || "" });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 sm:col-span-2"><Label className="text-xs">Batch #</Label><Input value={it.batch_no} onChange={e => updateItem(i, { batch_no: e.target.value })} placeholder="auto" /></div>
                <div className="col-span-6 sm:col-span-2"><Label className="text-xs">Expiry</Label><Input type="date" value={it.expiry_date} onChange={e => updateItem(i, { expiry_date: e.target.value })} /></div>
                <div className="col-span-4 sm:col-span-1"><Label className="text-xs">Qty</Label><Input type="number" value={it.qty} onChange={e => updateItem(i, { qty: e.target.value })} /></div>
                <div className="col-span-4 sm:col-span-1"><Label className="text-xs">Cost</Label><Input type="number" value={it.cost_price} onChange={e => updateItem(i, { cost_price: e.target.value })} /></div>
                <div className="col-span-3 sm:col-span-1"><Label className="text-xs">MRP</Label><Input type="number" value={it.mrp} onChange={e => updateItem(i, { mrp: e.target.value })} placeholder="—" /></div>
                <div className="col-span-1 flex justify-end"><Button variant="ghost" size="icon" onClick={() => removeItem(i)}><X className="w-4 h-4" /></Button></div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 border-t border-border pt-3">
          <div><Label>Other charges</Label><Input type="number" value={f.other_charges} onChange={e => setF(p => ({ ...p, other_charges: e.target.value }))} /></div>
          <div><Label>Discount</Label><Input type="number" value={f.discount} onChange={e => setF(p => ({ ...p, discount: e.target.value }))} /></div>
          <div>
            <Label>Payment</Label>
            <Select value={f.payment_method} onValueChange={v => setF(p => ({ ...p, payment_method: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["cash", "card", "upi", "credit", "other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Paid amount</Label><Input type="number" value={f.paid_amount} onChange={e => setF(p => ({ ...p, paid_amount: e.target.value }))} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-md bg-muted p-3"><div className="text-xs uppercase tracking-widest text-muted-foreground">Items total</div><div className="font-mono font-semibold text-lg">{inr(totals.items_total)}</div></div>
          <div className="rounded-md bg-primary/10 p-3"><div className="text-xs uppercase tracking-widest text-primary font-semibold">Grand total</div><div className="font-heading font-black text-2xl">{inr(totals.grand)}</div></div>
        </div>
        <div><Label>Notes</Label><Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="save-purchase"><ShoppingCart className="w-4 h-4 mr-1" />{busy ? "Saving…" : "Record purchase"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseDetail({ purchase, onClose, onDeleted }) {
  const del = async () => {
    if (!window.confirm("Delete this purchase? Only works if no stock has been used yet.")) return;
    try { await api.delete(`/purchases/${purchase.id}`); toast.success("Deleted"); onDeleted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!purchase} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="purchase-detail">
        {purchase && (
          <>
            <DialogHeader><DialogTitle>{purchase.purchase_no} · Bill {purchase.bill_no || "—"}</DialogTitle></DialogHeader>
            <div className="text-sm text-muted-foreground">{purchase.bill_date} · {purchase.supplier?.name || "Walk-in"} · {purchase.status}</div>
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Batch</TableHead><TableHead>Expiry</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {purchase.items.map((it, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{it.product_id.slice(0, 8)}</TableCell>
                    <TableCell className="font-mono text-xs">{it.batch_no}</TableCell>
                    <TableCell>{it.expiry_date || "—"}</TableCell>
                    <TableCell className="text-right">{it.qty}</TableCell>
                    <TableCell className="text-right font-mono">{inr(it.cost_price)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(it.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end">
              <div className="space-y-1 text-sm">
                <Row label="Items total" v={inr(purchase.items_total)} />
                {purchase.other_charges > 0 && <Row label="Other charges" v={inr(purchase.other_charges)} />}
                {purchase.discount > 0 && <Row label="Discount" v={`-${inr(purchase.discount)}`} />}
                <Row label="Grand total" v={inr(purchase.grand_total)} bold />
                <Row label="Paid" v={inr(purchase.paid_amount)} />
                {purchase.balance > 0 && <Row label="Balance" v={inr(purchase.balance)} />}
              </div>
            </div>
            {purchase.notes && <div className="text-sm bg-muted p-3 rounded">{purchase.notes}</div>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button variant="destructive" onClick={del} data-testid="delete-purchase"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, v, bold }) => (
  <div className="flex items-center justify-between gap-6">
    <span className={bold ? "font-semibold" : "text-muted-foreground"}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold text-lg" : ""}`}>{v}</span>
  </div>
);
